import { useMemo } from "react";
import {
  getHospitalClinic,
  getOrganization,
  useAdditionalInfo,
} from "@/stores/domains/additionalInfo/useAdditionalInfo";

/**
 * React Hook to get clinicId from user's selection in additionalInfo
 * This ensures all components get clinicId from user's explicit selection
 */
export const useClinicId = (): string | null => {
  // Subscribe to additionalInfo store to get reactive updates
  // Note: location is the clinic/hospital selection
  const location = useAdditionalInfo((state) => state.location);

  return useMemo(() => {
    return location?.value || null;
  }, [location]);
};

/**
 * React Hook to get organizationId from user's selection in additionalInfo
 * This ensures all components get organizationId from user's explicit selection
 */
export const useOrganizationId = (): string | null => {
  // Subscribe to additionalInfo store to get reactive updates
  const organization = useAdditionalInfo((state) => state.organization);

  return useMemo(() => {
    return organization?.value || null;
  }, [organization]);
};

/**
 * Get clinicId from user's selection in additionalInfo
 * This is useful for service functions that can't use hooks
 *
 * Note: Returns null silently if AdditionalInfo is not set yet.
 * This is expected during initial login flow before user selects clinic.
 */
export const getClinicId = (): string | null => {
  const hospitalClinic = getHospitalClinic();

  if (hospitalClinic?.value) {
    return hospitalClinic.value;
  }

  // Don't log error - this is expected during initial login
  // Error will be shown in UI if user tries to access features requiring clinic
  return null;
};

/**
 * Get organisationId from user's selection in additionalInfo
 * This is useful for service functions that can't use hooks
 *
 * Note: Returns null silently if AdditionalInfo is not set yet.
 * This is expected during initial login flow before user selects organization.
 */
export const getOrganisationId = (): string | null => {
  const organization = getOrganization();

  if (organization?.value) {
    return organization.value;
  }

  // Don't log error - this is expected during initial login
  // Error will be shown in UI if user tries to access features requiring organization
  return null;
};
